import type { TextFieldProps } from '@material-ui/core/TextField'
import _TextField from '@material-ui/core/TextField/index.js'
import cn from 'classnames'
import React from 'react'
import styled, { css } from 'styled-components'
import {
  alertErrorPrimary,
  backgroundColor,
  bordersColor,
  complementaryColor,
  darkGrayColor,
  primaryColor,
  softGrayColor,
} from 'uiComponents/constants/colors'
import {
  fontFamilyPrimary,
  fsXS,
  fsXXXS,
  fwMediumBold,
} from 'uiComponents/constants/typography'
import Icon, { IconSize } from '../Icon/Icon'
import { IconName } from '../Icon/IconName'

const TextField = (
  'default' in _TextField ? _TextField.default : _TextField
) as typeof _TextField

const StyledTextInput = styled(TextField)<Partial<IProps>>`
  &.filled {
    .MuiInputBase-root {
      background-color: 'transparent';

      .MuiOutlinedInput-notchedOutline {
        ${({ error, borderColor }) => {
          const defaultColor = borderColor || bordersColor

          return css`
            border-color: ${error ? alertErrorPrimary : defaultColor};
          `
        }};
        border-radius: 2px;
      }

      :focus {
        .MuiOutlinedInput-notchedOutline {
          ${({ error, borderColor }) => {
            const defaultColor = borderColor || 'transparent'

            return css`
              border-color: ${error ? alertErrorPrimary : defaultColor};
            `
          }};

          border-radius: 2px;
        }
      }
    }
  }

  .MuiInputBase-root {
    ${fontFamilyPrimary};
    ${({ alternative, transparent }) =>
      css`
        background-color: ${transparent || alternative
          ? 'transparent'
          : backgroundColor};
      `};
    border-radius: 2px;
    font-size: ${fsXS}px;
    color: ${({ fontColor }) => fontColor || primaryColor};
    ${fwMediumBold};

    .MuiOutlinedInput-notchedOutline {
      border-radius: 2px;
      border-color: ${backgroundColor};
      ${({ alternative }) =>
        alternative &&
        css`
          border-color: ${bordersColor};
        `};

      ${({ error }) =>
        error &&
        css`
          border-color: alertErrorPrimary;
        `};
    }

    &:hover {
      .MuiOutlinedInput-notchedOutline {
        border-color: ${darkGrayColor};
      }
    }

    &.Mui-focused {
      background-color: ${({ transparent }) =>
        transparent ? 'transparent' : complementaryColor};

      & > .MuiOutlinedInput-notchedOutline {
        ${({ error, borderColor }) => {
          const defaultColor = borderColor || primaryColor

          return css`
            border-color: ${error ? alertErrorPrimary : defaultColor};
          `
        }};
      }

      &:hover {
        border-color: ${primaryColor};
      }
    }
  }

  .MuiFormLabel-root {
    ${fontFamilyPrimary};
    color: ${({ placeholderColor }) => placeholderColor || darkGrayColor};
    font-size: ${fsXS}px;
    ${fwMediumBold};
    &.Mui-error {
      color: ${darkGrayColor};
    }

    &.MuiInputLabel-shrink {
      color: ${({ fontColor }) => fontColor || primaryColor};

      &.Mui-error {
        color: ${alertErrorPrimary};
      }
    }
  }

  .MuiFormHelperText-root {
    ${fontFamilyPrimary};
    ${fwMediumBold};
    font-size: ${fsXXXS}px;
    color: ${softGrayColor};
  }
`

interface IProps {
  alternative?: boolean
  value: string
  borderColor?: string
  fontColor?: string
  placeholderColor?: string
  transparent?: boolean
}

const TextInput: React.FC<TextFieldProps & IProps> = props => {
  const {
    variant,
    value,
    className,
    borderColor,
    transparent,
    ...passThroughProps
  } = props
  return (
    <StyledTextInput
      borderColor={borderColor}
      transparent={transparent}
      className={cn(
        {
          filled: Boolean(value) && value.length,
        },
        className,
      )}
      variant={'outlined'}
      InputProps={{
        endAdornment: passThroughProps.error ? (
          <Icon asset={IconName.IcAttentionRed} size={IconSize.S} />
        ) : undefined,
      }}
      {...passThroughProps}
    />
  )
}

export default TextInput
